import React, { useState } from "react";
import { ChevronDown, Zap } from "lucide-react";

/**
 * OrchestratorPanel — a lightweight, explicitly-beta banner that
 * communicates Wikifi's long-term vision: bring the user's favourite AI
 * tools into one connected workspace instead of forcing them to bounce
 * between separate products.
 *
 * Nothing here claims a live integration. The "Add to Orchestrator" action
 * on each tool icon (see MyStackRail) only records the user's intent; a
 * roadmap-facing signal, not a functional wiring.
 */
export default function OrchestratorPanel() {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-10 border border-[#E7EAF0] rounded-md bg-[#FAFBFC]" data-testid="orchestrator-panel">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full px-4 py-3 flex items-center gap-3 text-left"
        aria-expanded={open}
        data-testid="orchestrator-toggle"
      >
        <div className="w-8 h-8 rounded-md bg-[#0B1220] text-white flex items-center justify-center shrink-0">
          <Zap size={14}/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[12.5px] font-semibold text-[#0B1220]">Orchestrator</span>
          </div>
          <div className="text-[11.5px] text-[#5B6478] leading-relaxed mt-0.5">
            One workspace for every AI tool you actually use — coming soon.
          </div>
        </div>
        <ChevronDown size={14} className={`text-[#8A93A6] transition-transform ${open ? "rotate-180" : ""}`}/>
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 text-[12.5px] text-[#0B1220] leading-relaxed space-y-2.5 border-t border-[#E7EAF0]" data-testid="orchestrator-body">
          <p>
            Wikifi's goal is to <span className="font-semibold">integrate the AI tools you already love</span>{" "}
            — so instead of jumping between ChatGPT, Claude, Midjourney, Perplexity, Notion AI and
            twenty other tabs, you can drive them all from one workspace.
          </p>
          <p>
            This is not built yet. Today, the <span className="font-semibold">Add to Orchestrator</span>{" "}
            button on each tool below <span className="font-semibold">saves your intent</span> so we
            can prioritise the integrations most-requested by real users. It doesn't create a live
            connection to that third-party product yet.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-1">
            {[
              ["Track", "Every tool you pin as 'Add' tells us to prioritise its integration."],
              ["Connect", "As integrations land, your Orchestrator becomes a real launcher."],
              ["Automate", "Chain outputs across tools — Wikifi routes context automatically."],
            ].map(([title, body]) => (
              <div key={title} className="border border-[#E7EAF0] rounded-md bg-white p-2.5">
                <div className="text-[10.5px] font-bold uppercase tracking-wider text-[#8A93A6] mb-1">{title}</div>
                <div className="text-[11px] text-[#5B6478] leading-relaxed">{body}</div>
              </div>
            ))}
          </div>
          <p className="text-[10.5px] text-[#8A93A6] pt-1">
            Nothing here is claiming a live integration. Any tool without a real connection stays
            labelled <span className="font-semibold">Coming soon</span> until we ship it.
          </p>
        </div>
      )}
    </div>
  );
}
